import React from 'react'
import { Button, type ButtonProps, useMedia } from 'tamagui'
import type { DSButtonVariant } from '../types'
import CobuText from './CobuText'

type CobuButtonProps = Omit<ButtonProps, 'variant'> & {
  dsVariant?: DSButtonVariant
  title?: string
  loading?: boolean
  loadingText?: string
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
}

const getVariantStyles = (variant: DSButtonVariant) => {
  const styles = {
    primary: {
      backgroundColor: '$buttonBackground',
      borderWidth: 0,
      textColor: '$buttonText',
      pressStyle: { opacity: 0.9 },
      hoverStyle: { opacity: 0.8 },
      borderRadius: '$buttonPrimaryRadius'
    },
    secondary: {
      backgroundColor: '$buttonSecondaryBackground',
      borderWidth: '$strokeHairline',
      borderColor: '$buttonSecondaryBorderColor',
      textColor: '$buttonSecondaryText',
      pressStyle: { opacity: 0.9 },
      hoverStyle: { opacity: 0.8 },
      borderRadius: '$buttonSecondaryRadius'
    },
    tertiary: {
      backgroundColor: '$buttonTertiaryBackground',
      borderWidth: 0,
      textColor: '$buttonTertiaryText',
      pressStyle: { opacity: 0.9 },
      hoverStyle: { opacity: 0.8 },
      borderRadius: '$buttonTertiaryRadius'
    },
    tertiaryGray: {
      backgroundColor: '$buttonSecondaryBackground',
      borderWidth: '$strokeHairline',
      borderColor: '$buttonSecondaryBorderColor',
      textColor: '$buttonSecondaryText',
      pressStyle: { opacity: 0.95 },
      hoverStyle: { opacity: 0.98 },
      borderRadius: '$buttonTertiaryRadius'
    },
    textLink: {
      backgroundColor: 'transparent',
      borderWidth: 0,
      textColor: '$textLinkColor',
    },
    outline: {
      backgroundColor: 'transparent',
      borderWidth: 2,
      borderColor: '$cobuYellow',
      textColor: '$cobuYellow',
      pressStyle: { backgroundColor: '$cobuYellow05' },
      hoverStyle: { backgroundColor: '$cobuYellow05' },
    },
    ghost: {
      backgroundColor: 'transparent',
      borderWidth: 0,
      textColor: '$color',
      pressStyle: { opacity: 0.7 },
      hoverStyle: { opacity: 0.8 },
    },
  }
  return (styles as any)[variant] ?? styles.primary
}

export default function CobuButton({
  dsVariant = 'primary',
  title,
  loading = false,
  loadingText,
  iconLeft,
  iconRight,
  disabled,
  children,
  ...props
}: CobuButtonProps) {
  const media = useMedia()
  const variantStyles = getVariantStyles(dsVariant)
  const isTertiarySized = dsVariant === 'outline' || dsVariant === 'tertiary' || dsVariant === 'tertiaryGray'
  const isPrimarySized = dsVariant === 'primary' || dsVariant === 'secondary'
  const buttonHeight = (isTertiarySized
    ? ('$buttonTertiaryHeight' as any)
    : (isPrimarySized ? ('$buttonPrimaryHeight' as any) : undefined))
  const borderRadius = (dsVariant === 'tertiary' || dsVariant === 'tertiaryGray' || dsVariant === 'outline'
    ? ('$buttonTertiaryRadius' as any)
    : dsVariant === 'primary'
      ? ('$buttonPrimaryRadius' as any)
      : dsVariant === 'secondary'
        ? ('$buttonSecondaryRadius' as any)
        : undefined)
  return (
    <Button
      height={buttonHeight}
      borderRadius={borderRadius}
      backgroundColor={disabled ? '$buttonDisabledBackground' : variantStyles.backgroundColor as any}
      borderWidth={(variantStyles as any).borderWidth as any}
      borderColor={(variantStyles as any).borderColor}
      disabled={!!disabled || loading}
      {...props}
    >
      {iconLeft && !loading && iconLeft}
      {title ? (
        <CobuText
          variant="label"
          color={disabled ? ('$buttonDisabledText' as any) : (variantStyles.textColor as any)}
          fontWeight="bold"
          fontSize={media.gtMd ? ('$4' as any) : ('$3' as any)}
        >
          {loading && loadingText ? loadingText : title}
        </CobuText>
      ) : (
        children
      )}
      {iconRight && !loading && iconRight}
    </Button>
  )
}


